import { useCallback, useEffect, useState } from "react";
import {
	AlertTriangle,
	Check,
	Copy,
	Loader2,
	Plug,
	Undo2,
	Unplug,
} from "lucide-react";
import { toast } from "sonner";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "../ui/card";
import { Button } from "../ui/button";
import {
	describeScope,
	disconnectMcpClient,
	getMcpConnections,
} from "../../lib/mcp";
import { getRestorePoints, undoAgentSession } from "../../lib/aiAgent";
import { formatDate } from "../../lib/utils";
import { McpSetupGuide } from "./McpSetupGuide";

/**
 * Connettore MCP: l'URL da incollare nel client e i collegamenti attivi.
 * Spec: docs/ai-program/10-byok-and-mcp-rework.md (Fase 3).
 *
 * E' l'alternativa economica all'assistente in-app: invece di pagare i token
 * con una chiave API, il coach usa l'abbonamento Claude o ChatGPT che ha gia'.
 */
/**
 * I punti a cui si può tornare indietro.
 *
 * Esiste per il connettore: dentro l'app l'annullamento sta in fondo al
 * messaggio dell'assistente, qui il lavoro succede dentro Claude o ChatGPT e
 * non c'è nessun messaggio nostro su cui appoggiarlo. Senza questo elenco lo
 * scatto verrebbe preso e non sarebbe raggiungibile da nessuna parte, che è
 * come non averlo.
 */
/**
 * Cosa copre un punto di ripristino, in parole: programmi, modelli prestativi
 * (un modello nato quella giornata torna a non esistere) e categorie di peso.
 */
export function restorePointTitles(point) {
	return [
		...(point.programs || []).map((program) => program.title || program.program_id),
		...(point.models || []).map(
			(model) =>
				`${model.created_in_session ? "nuovo modello " : "modello "}${model.name || model.model_id}`,
		),
		...(point.sports || []).map((sport) => `categorie di ${sport.label || sport.sport}`),
	].join(", ");
}

/**
 * Il client ha l'elenco di strumenti di oggi? Claude e ChatGPT tengono la
 * fotografia presa al collegamento, e chi resta indietro lavora coi pezzi
 * vecchi senza saperlo (backend/services/mcp/catalog_sync.py). Da un
 * collegamento cosi' il server non esegue nessun tool, e l'assistente lo
 * spiega al coach: qui lo si vede anche senza chiedergli niente.
 */
export function CatalogStatus({ catalog }) {
	if (!catalog || catalog.status === "current") return null;
	const stale = catalog.status === "stale";
	const parts = [];
	if (stale && catalog.new_tools)
		parts.push(
			`${catalog.new_tools} ${catalog.new_tools === 1 ? "strumento nuovo" : "strumenti nuovi"}`,
		);
	if (stale && catalog.changed_tools)
		parts.push(
			`${catalog.changed_tools} ${catalog.changed_tools === 1 ? "cambiato" : "cambiati"}`,
		);
	return (
		<p
			className="mt-0.5 flex items-start gap-1 text-xs text-amber-500"
			data-testid="mcp-catalog-stale"
		>
			<AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
			<span>
				{stale
					? `Elenco strumenti da aggiornare${parts.length ? ` (${parts.join(", ")})` : ""}.`
					: "Non sappiamo se l'elenco strumenti è aggiornato."}{" "}
				Finché non lo aggiorni l'assistente non lavora: alla prima richiesta
				si ferma e ti spiega come fare. Aggiorna il connettore dalle
				impostazioni del client, poi apri una conversazione nuova.
			</span>
		</p>
	);
}

function RestorePoints({ points, onRestored }) {
	const [busy, setBusy] = useState(null);

	if (!points.length) return null;

	const restore = async (point) => {
		const titles = restorePointTitles(point);
		if (
			!window.confirm(
				`${titles} torna com'era prima di queste modifiche. ` +
					"Quello che hai cambiato a mano nel frattempo andrà perso. Procedo?",
			)
		) {
			return;
		}
		setBusy(point.session_id);
		try {
			await undoAgentSession(point.session_id);
			toast.success("Ripristinato");
			onRestored?.();
		} catch (error) {
			toast.error(error.message || "Ripristino non riuscito");
		} finally {
			setBusy(null);
		}
	};

	return (
		<div className="space-y-2">
			<p className="text-xs uppercase tracking-wide text-muted-foreground">
				Torna indietro
			</p>
			<p className="text-xs text-muted-foreground">
				Prima di ogni modifica fatta da un assistente salviamo com'era il
				programma o il modello prestativo. Gli scatti durano due giorni.
			</p>
			{points.map((point) => (
				<div
					key={point.session_id}
					className="flex items-center justify-between gap-3 rounded-sm border border-border bg-secondary/40 p-3"
				>
					<div className="min-w-0">
						<p className="truncate text-sm">{restorePointTitles(point)}</p>
						<p className="truncate text-xs text-muted-foreground">
							{point.source === "mcp" ? "Da un client collegato" : "Dalla chat"}
							{point.created_at ? ` · ${formatDate(point.created_at)}` : ""}
						</p>
					</div>
					<Button
						type="button"
						variant="ghost"
						size="sm"
						disabled={busy === point.session_id}
						onClick={() => restore(point)}
						className="shrink-0 text-muted-foreground hover:text-foreground"
					>
						<Undo2 className="mr-1.5 h-4 w-4" />
						{busy === point.session_id ? "Ripristino…" : "Ripristina"}
					</Button>
				</div>
			))}
		</div>
	);
}

export function McpConnectionsCard() {
	const [state, setState] = useState(null);
	const [restorePoints, setRestorePoints] = useState([]);
	const [copied, setCopied] = useState(false);

	const refresh = useCallback(async () => {
		try {
			setState(await getMcpConnections());
		} catch {
			setState({ enabled: false, connections: [] });
		}
		try {
			const data = await getRestorePoints();
			setRestorePoints(data.restore_points || []);
		} catch {
			// Un elenco che non si carica non deve nascondere il resto della
			// card: il connettore resta usabile.
			setRestorePoints([]);
		}
	}, []);

	useEffect(() => {
		refresh();
	}, [refresh]);

	const copyUrl = async () => {
		try {
			await navigator.clipboard.writeText(state.mcp_url);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch {
			toast.error("Copia non riuscita: seleziona l'indirizzo a mano");
		}
	};

	const disconnect = async (clientId, clientName) => {
		try {
			await disconnectMcpClient(clientId);
			await refresh();
			toast.success(`${clientName} scollegato`);
		} catch (error) {
			toast.error(error.message || "Impossibile scollegare");
		}
	};

	if (!state) return null;

	return (
		<Card
			className="bg-card border-border rounded-sm"
			data-testid="mcp-connections"
		>
			<CardHeader>
				<CardTitle className="font-heading text-xl flex items-center gap-2">
					<Plug className="w-5 h-5 text-volt" />
					Collega il tuo Claude o ChatGPT
				</CardTitle>
				<CardDescription>
					Lavora dal tuo assistente abituale e trova il programma già scritto
					qui dentro, senza ricopiare niente. Usa il tuo abbonamento: nessun
					costo aggiuntivo.
				</CardDescription>
			</CardHeader>

			<CardContent className="space-y-4">
				{!state.enabled ? (
					<p className="text-sm text-muted-foreground">
						Il connettore è disattivato su questo server.
					</p>
				) : (
					<>
						<div className="space-y-2">
							<p className="text-xs uppercase tracking-wide text-muted-foreground">
								Indirizzo del connettore
							</p>
							<div className="flex gap-2">
								<code className="flex-1 truncate rounded-sm border border-border bg-secondary/60 px-3 py-2 font-mono text-xs">
									{state.mcp_url}
								</code>
								<Button
									type="button"
									variant="outline"
									size="icon"
									onClick={copyUrl}
									className="shrink-0 rounded-sm"
									aria-label="Copia l'indirizzo"
								>
									{copied ? (
										<Check className="h-4 w-4 text-volt" />
									) : (
										<Copy className="h-4 w-4" />
									)}
								</Button>
							</div>
							<p className="text-xs text-muted-foreground">
								Questo è l'unico indirizzo che ti serve: si incolla nel client
								una volta sola.
							</p>
						</div>

						<McpSetupGuide />

						<div className="space-y-1">
							<p className="text-xs uppercase tracking-wide text-muted-foreground">
								Cosa puoi chiedergli
							</p>
							<p className="text-xs text-muted-foreground">
								Costruire un programma e scriverlo direttamente qui, leggere
								atleti, risultati, profilo prestativo e diario, cercare in
								libreria esercizi veri — con i loro id, senza inventarli — e
								tenere il calendario gare. Il programma resta non pubblicato
								finché non lo pubblichi tu.
							</p>
						</div>

						{state.connections.length > 0 && (
							<div className="space-y-2">
								<p className="text-xs uppercase tracking-wide text-muted-foreground">
									Collegati
								</p>
								{state.connections.map((connection) => (
									<div
										key={connection.client_id}
										className="flex items-center justify-between gap-3 rounded-sm border border-volt/40 bg-volt/10 p-3"
									>
										<div className="min-w-0">
											<p className="truncate text-sm font-medium">
												{connection.client_name}
											</p>
											<p className="truncate text-xs text-muted-foreground">
												{connection.scopes.map(describeScope).join(" · ")}
											</p>
											{connection.scopes.includes("programs:modify") && (
												<p className="mt-0.5 flex items-center gap-1 text-xs text-blaze">
													<AlertTriangle className="h-3 w-3 shrink-0" />
													Può cancellare dai programmi
												</p>
											)}
											{connection.scopes.includes("models:write") && (
												<p className="mt-0.5 flex items-center gap-1 text-xs text-blaze">
													<AlertTriangle className="h-3 w-3 shrink-0" />
													Può modificare i modelli prestativi
												</p>
											)}
											{/* Collegato prima del 2026-10-27, o senza la spunta: i
											    permessi arrivano solo ricollegando (il rinnovo del
											    token tiene quelli di prima). */}
											{!connection.scopes.includes("models:write") && (
												<p
													className="mt-0.5 text-xs text-muted-foreground"
													data-testid="mcp-missing-models"
												>
													Per configurare i modelli prestativi scollegalo e
													ricollegalo dal client: il consenso proporrà il
													permesso nuovo.
												</p>
											)}
											<CatalogStatus catalog={connection.catalog} />
										</div>
										<Button
											type="button"
											variant="ghost"
											size="sm"
											onClick={() =>
												disconnect(connection.client_id, connection.client_name)
											}
											className="shrink-0 text-muted-foreground hover:text-blaze"
										>
											<Unplug className="mr-1.5 h-4 w-4" />
											Scollega
										</Button>
									</div>
								))}
							</div>
						)}

						<RestorePoints points={restorePoints} onRestored={refresh} />
					</>
				)}
			</CardContent>
		</Card>
	);
}
